import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { RiCheckLine } from "@itandi/itandi-bb-ui";
import clsx from "clsx";
import type { ReactNode } from "react";
import React from "react";
import Styles from "./ProgressIndicator.module.scss";

export type IndicatorItem = {
	label: string;
};
type Props = DeepReadonly<{
	currentIndex: number;
	items: IndicatorItem[];
}>;
type ItemProps = DeepReadonly<{
	currentIndex: number;
	index: number;
	item: IndicatorItem;
	itemsLength: number;
}>;

type Status = "yet" | "current" | "done";

const statusStyles: Record<Status, string> = {
	current: Styles.Current,
	done: Styles.Done,
	yet: Styles.Yet
};

type Position = "first" | "between" | "last";

const positionStyles: Record<Position, string> = {
	between: Styles.Between,
	first: Styles.First,
	last: Styles.Last
};

function ProgressIndicatorItem({
	item,
	index,
	itemsLength,
	currentIndex
}: ItemProps) {
	const status = React.useMemo((): Status => {
		if (currentIndex === itemsLength - 1) {
			// 最後のステータスに到達したら完了扱い
			return "done";
		}
		if (currentIndex === index) {
			return "current";
		}
		if (currentIndex > index) {
			return "done";
		}
		return "yet";
	}, [currentIndex, index, itemsLength]);
	const position = React.useMemo((): Position => {
		if (index === 0) {
			return "first";
		}
		if (index === itemsLength - 1) {
			return "last";
		}
		return "between";
	}, [index, itemsLength]);
	return (
		<div
			className={clsx(
				Styles.ProgressIndicatorItem,
				positionStyles[position],
				statusStyles[status]
			)}
		>
			<div className={Styles.IndicatorItem}>
				<span
					className={clsx(
						Styles.Line,
						Styles.Left,
						positionStyles[position],
						statusStyles[status]
					)}
				/>
				<span className={Styles.Icon}>
					{status === "done" ? (
						<RiCheckLine className={Styles.CheckIcon} />
					) : (
						<>{index + 1}</>
					)}
				</span>
				<span
					className={clsx(
						Styles.Line,
						Styles.Right,
						positionStyles[position],
						statusStyles[status]
					)}
				/>
			</div>
			<div className={Styles.Label}>{item.label}</div>
		</div>
	);
}

export function ProgressIndicator({ items, currentIndex }: Props) {
	return (
		<div className={Styles.ProgressIndicator}>
			{items.map(
				(item: IndicatorItem, index: number): ReactNode => (
					<ProgressIndicatorItem
						currentIndex={currentIndex}
						index={index}
						item={item}
						itemsLength={items.length}
						key={`${index}-${item.label}`}
					/>
				)
			)}
		</div>
	);
}
